import { createFileRoute, redirect } from '@tanstack/react-router'
import { createServerFn } from '@tanstack/react-start'

const handleGithubCallback = createServerFn({ method: 'GET' })
  .inputValidator((data: { code: string }) => data)
  .handler(async ({ data }) => {
    const { useSession } = await import('@tanstack/react-start/server')
    const { sessionConfig } = await import('#/lib/auth')

    const { code } = data

    if (!code) {
      return { redirectTo: '/?auth_error=no_code' }
    }

    // Exchange code for access token
    const tokenResponse = await fetch(
      'https://github.com/login/oauth/access_token',
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          client_id: process.env.VITE_GITHUB_CLIENT_ID,
          client_secret: process.env.GITHUB_CLIENT_SECRET,
          code,
        }),
      },
    )

    const tokenData = (await tokenResponse.json()) as {
      access_token?: string
      error?: string
    }

    if (!tokenData.access_token) {
      return { redirectTo: '/?auth_error=token_exchange_failed' }
    }

    // Fetch user profile
    const userResponse = await fetch('https://api.github.com/user', {
      headers: {
        Authorization: `Bearer ${tokenData.access_token}`,
        Accept: 'application/vnd.github.v3+json',
      },
    })

    const userData = (await userResponse.json()) as {
      login: string
      id: number
      avatar_url: string
    }

    if (!userData.login) {
      return { redirectTo: '/?auth_error=user_fetch_failed' }
    }

    // Check allowlist
    const allowed = (process.env.ALLOWED_GITHUB_USERS || '')
      .split(',')
      .map((u) => u.trim().toLowerCase())

    if (!allowed.includes(userData.login.toLowerCase())) {
      return { redirectTo: '/?auth_error=unauthorized' }
    }

    // Set session
    const session = await useSession(sessionConfig)
    await session.update({
      githubUser: userData.login,
      githubId: userData.id,
      avatarUrl: userData.avatar_url,
    })

    return { redirectTo: '/?edit=true' }
  })

export const Route = createFileRoute('/api/auth/callback')({
  validateSearch: (search: Record<string, unknown>) => ({
    code: (search.code as string) || '',
  }),
  beforeLoad: async ({ search }) => {
    const result = await handleGithubCallback({ data: { code: search.code } })
    throw redirect({ href: result.redirectTo })
  },
  component: () => null,
})
